import React, { useEffect, useRef, useState } from 'react';
import { animated, useTransition, useSpring } from 'react-spring';
import styled, { css } from 'styled-components';
import illustrations from '../../../../../../../Illustration/components';
import JustificationContainer from '../../../../../../../JustificationContainer';
import { Heading2, Heading4 } from '../../../../../../../Typography';
import Illustration from '../../../../../../../Illustration';

export type Props = {
  dataTestId?: string;
  header: string;
  description?: string | null;
  illustration?: keyof typeof illustrations;
} & React.HtmlHTMLAttributes<HTMLDivElement>;

const ILLUSTRATION_SIZE = 64;
const HighlightedHeader: React.FC<Props> = ({
  dataTestId,
  header,
  description,
  illustration,
  ...rest
}) => {
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const showDescription = !header || (!!header && header.length < 34);
  const descriptionToShow = showDescription && !!description ? description : null;

  const [containerSpring, containerApi] = useSpring(() => ({
    height: 'auto' as number | 'auto',
    config: { duration: 300 },
  }));

  const headerTransitions = useTransition(header, {
    from: {
      opacity: 0,
      transform: 'translateY(-100%) scale(0.5)',
    },
    enter: {
      opacity: 1,
      transform: 'translateY(0%) scale(1)',
    },
    leave: {
      opacity: 0,
      transform: 'translateY(100%) scale(0.3)',
    },
    config: {
      tension: 220,
      friction: 18,
      mass: 1,
      duration: 300,
    },
  });

  useEffect(() => {
    if (!measureRef.current) return;

    const nextHeight = measureRef.current.offsetHeight;
    if (!nextHeight) return;

    if (contentHeight === null) {
      setContentHeight(nextHeight);
      containerApi.set({ height: nextHeight });
      return;
    }

    if (nextHeight !== contentHeight) {
      setContentHeight(nextHeight);
      containerApi.start({ height: nextHeight });
    }
  }, [header, description, illustration, contentHeight, containerApi]);

  return (
    <Container style={containerSpring} data-testid={dataTestId} {...rest}>
      {/* Static, hidden content to reserve layout height */}
      <JustificationContainer
        align="center"
        ref={measureRef}
        style={{
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <StyledIllustration name={illustration || 'bb-wc'} />
        <JustificationContainer
          direction="column"
          margin={[null, null, null, 'base']}
        >
          <StyledHeading2>{header}</StyledHeading2>
          {descriptionToShow && (
            <StyledHeading4 $margin={[null]}>{descriptionToShow}</StyledHeading4>
          )}
        </JustificationContainer>
      </JustificationContainer>

      {/* Animated content absolutely positioned on top */}
      {headerTransitions((styles, _item) => (
        <AnimatedHeader style={styles}>
          <JustificationContainer align="center">
            <StyledIllustration name={illustration || 'bb-wc'} />
            <JustificationContainer
              direction="column"
              margin={[null, null, null, 'base']}
            >
              <StyledHeading2>{header}</StyledHeading2>
              {descriptionToShow && (
                <StyledHeading4 $margin={[null]}>{descriptionToShow}</StyledHeading4>
              )}
            </JustificationContainer>
          </JustificationContainer>
        </AnimatedHeader>
      ))}
    </Container>
  );
};

const Container = styled(animated.div)<{}>(
  ({ theme }) => css`
    box-sizing: content-box;
    position: relative;
    overflow: hidden;
    background-color: ${theme.color('primary')};
    padding: ${theme.space('m')};
    border-radius: ${theme.getSystem().border.radius.base}
      ${theme.getSystem().border.radius.base} 0 0;
  `,
);

const AnimatedHeader = styled(animated.div)<{}>(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    padding: ${theme.space('m')};
  `,
);

const StyledHeading2 = styled(Heading2)<{}>(
  ({ theme }) => css`
    color: ${theme.color('primary', 'text')};
  `,
);

const StyledHeading4 = styled(Heading4)<{}>(
  ({ theme }) => css`
    color: ${theme.color('primary', 'text')};
  `,
);

const StyledIllustration = styled(Illustration)<{}>(
  ({ theme }) => css`
    svg {
      height: ${ILLUSTRATION_SIZE}px;
      width: ${ILLUSTRATION_SIZE}px;
      color: ${theme.color('primary', 'text')};
    }
  `,
);

export default HighlightedHeader;
